/**
 * Process items in parallel batches with concurrency control.
 *
 * Instead of processing all items at once (overwhelming the server) or
 * one at a time (slow), this processes items in batches of a specified size.
 *
 * @param items - Array of items to process
 * @param processor - Async function to process each item
 * @param batchSize - Number of items to process in parallel (default: 3)
 * @returns Array of results in the same order as input items
 */
export async function batchProcess<T, R>(
    items: T[],
    processor: (item: T) => Promise<R>,
    batchSize: number = 3
): Promise<R[]> {
    const results: R[] = [];

    for (let i = 0; i < items.length; i += batchSize) {
        const batch = items.slice(i, i + batchSize);
        const batchResults = await Promise.all(batch.map(processor));
        results.push(...batchResults);
    }

    return results;
}
